import axiosInstance from '../axiosClient';

/**
 * Mission Template API Service
 * Handles all mission template CRUD operations
 */

/**
 * Create a mission template from an existing operation
 * @param {Object} templateData - Template data
 * @param {string} templateData.name - Unique template name
 * @param {string} templateData.description - Template description
 * @param {string} templateData.operationId - Source operation ID
 * @param {boolean} templateData.isShared - Whether to share with organization
 * @param {string} token - Auth token
 * @param {string} organizationUuid - Organization UUID (optional)
 * @returns {Promise<Object>} Created template
 */
export const createMissionTemplate = async (templateData, token, organizationUuid = null) => {
    const headers = {
        'Authorization': `Bearer ${token}`
    };

    if (organizationUuid) {
        headers['x-organization-id'] = organizationUuid;
    }

    const response = await axiosInstance.post(
        '/mission-templates',
        templateData,
        { headers }
    );
    return response.data;
};

/**
 * Get all mission templates accessible to the user
 * For pilots: Returns their own templates + shared templates
 * For owners/managers: Returns all templates in the organization
 * @param {string} token - Auth token
 * @param {Object} filters - Optional filters
 * @param {boolean} filters.sharedOnly - Get only shared templates
 * @param {string} filters.createdBy - Filter by creator user ID
 * @param {string} organizationUuid - Organization UUID (optional)
 * @returns {Promise<Array>} List of templates
 */
export const getMissionTemplates = async (token, filters = {}, organizationUuid = null) => {
    const params = new URLSearchParams();

    if (filters.sharedOnly !== undefined) {
        params.append('sharedOnly', filters.sharedOnly);
    }
    if (filters.createdBy) {
        params.append('createdBy', filters.createdBy);
    }

    const headers = {
        'Authorization': `Bearer ${token}`
    };

    if (organizationUuid) {
        headers['x-organization-id'] = organizationUuid;
    }

    const response = await axiosInstance.get(
        `/mission-templates?${params.toString()}`,
        { headers }
    );
    return response.data;
};

/**
 * Get a specific mission template by ID
 * @param {string} templateId - Template ID
 * @param {string} token - Auth token
 * @param {string} organizationUuid - Organization UUID (optional)
 * @returns {Promise<Object>} Template details
 */
export const getMissionTemplateById = async (templateId, token, organizationUuid = null) => {
    const headers = {
        'Authorization': `Bearer ${token}`
    };

    if (organizationUuid) {
        headers['x-organization-id'] = organizationUuid;
    }

    const response = await axiosInstance.get(
        `/mission-templates/${templateId}`,
        { headers }
    );
    return response.data;
};

/**
 * Update a mission template
 * Only the creator or owners/managers can update
 * @param {string} templateId - Template ID
 * @param {Object} updates - Fields to update
 * @param {string} updates.name - Updated name (must be unique)
 * @param {string} updates.description - Updated description
 * @param {boolean} updates.isShared - Updated sharing setting
 * @param {string} token - Auth token
 * @param {string} organizationUuid - Organization UUID (optional)
 * @returns {Promise<Object>} Updated template
 */
export const updateMissionTemplate = async (templateId, updates, token, organizationUuid = null) => {
    const headers = {
        'Authorization': `Bearer ${token}`
    };

    if (organizationUuid) {
        headers['x-organization-id'] = organizationUuid;
    }

    const response = await axiosInstance.put(
        `/mission-templates/${templateId}`,
        updates,
        { headers }
    );
    return response.data;
};

/**
 * Delete a mission template
 * Only the creator or owners/managers can delete
 * @param {string} templateId - Template ID
 * @param {string} token - Auth token
 * @param {string} organizationUuid - Organization UUID (optional)
 * @returns {Promise<void>}
 */
export const deleteMissionTemplate = async (templateId, token, organizationUuid = null) => {
    const headers = {
        'Authorization': `Bearer ${token}`
    };

    if (organizationUuid) {
        headers['x-organization-id'] = organizationUuid;
    }

    await axiosInstance.delete(
        `/mission-templates/${templateId}`,
        { headers }
    );
};

/**
 * Check if a template name is unique
 * @param {string} name - Template name to check
 * @param {string} token - Auth token
 * @param {string} excludeId - Template ID to exclude from check (for updates)
 * @param {string} organizationUuid - Organization UUID (optional)
 * @returns {Promise<boolean>} True if name is available
 */
export const checkTemplateNameUnique = async (name, token, excludeId = null, organizationUuid = null) => {
    const params = new URLSearchParams({ name });
    if (excludeId) {
        params.append('excludeId', excludeId);
    }

    const headers = {
        'Authorization': `Bearer ${token}`
    };

    if (organizationUuid) {
        headers['x-organization-id'] = organizationUuid;
    }

    const response = await axiosInstance.get(
        `/mission-templates/check-name?${params.toString()}`,
        { headers }
    );
    return response.data.available;
};

/**
 * Get templates created from a specific operation
 * @param {string} operationId - Operation ID
 * @param {string} token - Auth token
 * @param {string} organizationUuid - Organization UUID (optional)
 * @returns {Promise<Array>} Templates created from this operation
 */
export const getTemplatesByOperation = async (operationId, token, organizationUuid = null) => {
    const headers = {
        'Authorization': `Bearer ${token}`
    };

    if (organizationUuid) {
        headers['x-organization-id'] = organizationUuid;
    }

    const response = await axiosInstance.get(
        `/mission-templates/by-operation/${operationId}`,
        { headers }
    );
    return response.data;
};
